// Hook for fetching opportunities — all filtering, search, and sorting are BE-side.

import { useState, useEffect, useCallback } from 'react';
import type { Opportunity, OpportunityQueryParams, PaginatedMeta } from '../types';
import { opportunitiesService, type ApiError } from '../opportunities.service';
import { captureException } from '@startus/lib';

const DEFAULT_META: PaginatedMeta = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
  hasPrevious: false,
  hasNext: false,
};

export interface UseOpportunitiesResult {
  opportunities: Opportunity[];
  meta: PaginatedMeta;
  loading: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useOpportunities(params: OpportunityQueryParams = {}): UseOpportunitiesResult {
  const [page, setPageData] = useState<{ items: Opportunity[]; meta: PaginatedMeta } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Stable key from all params — triggers refetch when any param changes
  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await opportunitiesService.getAll(
        JSON.parse(paramsKey) as OpportunityQueryParams
      );
      setPageData({ items: result.data, meta: result.meta });
    } catch (err) {
      captureException(err);
      setError(err as ApiError);
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    opportunities: page?.items ?? [],
    meta: page?.meta ?? DEFAULT_META,
    loading,
    error,
    refetch: fetch,
  };
}
